import {Injectable} from '@angular/core';
import {PictureUtil} from '../utils/picture';
import type {GlobalWorkerOptionsType} from 'pdfjs-dist/types/src/display/worker_options';
import type {getDocument as GetDocumentFunction} from 'pdfjs-dist';

export type ImageOutput = 'image/png' | 'image/jpeg';

export interface PdfJsLibDefault {
    GlobalWorkerOptions: GlobalWorkerOptionsType,
    getDocument: typeof GetDocumentFunction
}

@Injectable({
    providedIn: 'root'
})
export class PdfReaderService {

    // The workerSrc option should always be set,
    // in order to prevent any issues when using the PDF.js library
    async pdfJsLib() {
        const pdfJsLib: PdfJsLibDefault = (await import('pdfjs-dist')).default;
        pdfJsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
        return pdfJsLib;
    }

    async toImageBlob(input: File, imageOutput: ImageOutput = 'image/png'): Promise<Blob[]> {
        const pdfJsLib = await this.pdfJsLib();

        const pdfPagePromises: Promise<unknown>[] = [];
        const imageBlobs: Blob[] = [];

        try {
            const pdfDocument = await pdfJsLib.getDocument(URL.createObjectURL(input)).promise;

            for (let i = 1; i < pdfDocument.numPages + 1; ++i) {
                pdfPagePromises.push(pdfDocument.getPage(i).then(async (page) => {
                    const viewport = page.getViewport({scale: 1, rotation: 0, dontFlip: false});

                    // Prepare canvas using PDF page dimensions
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    if (context === null) {
                        throw new Error('Could not get canvas context');
                    }

                    canvas.height = viewport.viewBox[3];
                    canvas.width = viewport.viewBox[2];

                    // Render PDF page into canvas context
                    await page.render({
                        canvasContext: context,
                        viewport: viewport
                    }).promise;
                    imageBlobs.push(PictureUtil.dataURItoBlob(canvas.toDataURL(imageOutput)));
                }));
            }

            // Wait for all PDF page promises
            await Promise.all(pdfPagePromises);
            return imageBlobs;
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
}
